import { sendReceiptMail } from '@/api';
import { useAppSelector } from '@/hooks/useReduxHooks';
import { IGlobalState } from '@/store';
import { Icon, Input, Modal, Popover } from '@successmode-ktp/kui';
import { AxiosError } from 'axios';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useMutation } from 'react-query';
import styled from 'styled-components';
import html2pdf from 'html2pdf.js';
import { IFormatedReceiptInfo } from '@/types/common.type';
import ReceiptForPrint from '@/components/Receipt/ReceiptForPrint';
import useToast from '@/hooks/useToast';

interface IPrintDataInfo {
  refundIndex: number;
  purchaseSn: string;
  barcodeS3Path: string | null;
  rowDataArr: Array<IFormatedReceiptInfo>;
}

type Props = {
  isOpen: boolean;
  onClose: () => void;
  receiptInfo: IPrintDataInfo[];
};

function ReceiptMailModal({ isOpen, onClose, receiptInfo }: Props) {
  const toast = useToast();
  const { passportNumber, nationality, email } = useAppSelector(
    (state) => state,
  ) as IGlobalState;
  const [inputEmail, setInputEmail] = useState('');
  const [error, setError] = useState('');
  const elementRef = useRef(null);

  useEffect(() => {
    setInputEmail(email || '');
  }, [email]);

  const handleChangeEmail = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setInputEmail(value);

    const emailTest = /^[a-zA-Z0-9+-\_.]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;
    const isValid = value.match(emailTest);
    if (!isValid) {
      setError(
        nationality === 'KOR'
          ? '올바르지 않은 이메일 형식입니다. 다시 입력해주세요.'
          : 'The email format is invalid. Please enter it again.',
      );
    } else {
      setError('');
    }
  };

  const { mutateAsync: sendReceiptMailMutate } = useMutation<
    string,
    AxiosError,
    FormData
  >((payload) => sendReceiptMail(payload), {
    retry: false,
    onSuccess: () => {
      toast.open({
        id: 'success-mail',
        message:
          nationality === 'KOR'
            ? '환급전표가 메일로 전송되었습니다.'
            : 'Receipts sent to your email',
      });
      onClose();
    },
    onError: () => {
      alert('환급전표가 메일로 전송되는데 실패하였습니다.');
      onClose();
    },
  });

  const sendEmail = async (formData: FormData) => {
    await sendReceiptMailMutate(formData);
  };

  const convertComponentToPdf = async () => {
    const pdfOptions = {
      margin: 0,
      filename: `ktp_${passportNumber}_refund_statement.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' },
    };
    const pdfBlob = await html2pdf()
      .set(pdfOptions)
      .from(elementRef.current)
      .toPdf()
      .output('blob');
    const formData = new FormData();
    const requestBlob = new Blob(
      [
        JSON.stringify({
          email: inputEmail,
          passportNumber,
          isAirport: true,
          isKorean: nationality === 'KOR',
        }),
      ],
      {
        type: 'application/json',
      },
    );
    formData.append(
      `receiptPdf`,
      pdfBlob as Blob,
      `ktp_${passportNumber}_refund_statement.pdf`,
    );
    formData.append('request', requestBlob);
    sendEmail(formData);
  };

  const handleSendMail = () => {
    convertComponentToPdf();
  };

  const isDisabled = !inputEmail || !!error;

  const nationIndex = nationality === 'KOR' ? 1 : 0;
  const text = {
    title: [
      'Enter your Email address',
      '이메일 주소를 입력 후 전송을 눌러주세요.',
    ],
    placeholder: ['Enter your Email address', '이메일 주소를 입력해주세요.'],
    close: ['Close', '닫기'],
    send: ['Send', '전송하기'],
  };

  return (
    <>
      <Popover
        isOpen={isOpen}
        hasBackdrop
        style={{ padding: '24px', borderRadius: '12px' }}
      >
        <Modal.Header>
          <Title>
            <Icon.Mail size={20} fillColor='#333333' />
            {text.title[nationIndex]}
          </Title>
        </Modal.Header>
        <Content>
          <Input
            size='lg'
            value={inputEmail}
            onChange={handleChangeEmail}
            placeholder={text.placeholder[nationIndex]}
            error={error}
          />
        </Content>
        <Modal.Footer>
          <Modal.Button variant='cancel' onClick={onClose}>
            {text.close[nationIndex]}
          </Modal.Button>
          <Modal.Button
            variant='ok'
            onClick={handleSendMail}
            disabled={isDisabled}
          >
            {text.send[nationIndex]}
          </Modal.Button>
        </Modal.Footer>
      </Popover>
      <HiddenWrap>
        <div ref={elementRef}>
          {receiptInfo?.map((item, index) => (
            <ReceiptForPrint
              key={index}
              info={item}
              order={index + 1}
              lastIndex={receiptInfo?.length}
            />
          ))}
        </div>
      </HiddenWrap>
    </>
  );
}

const Title = styled.h4`
  display: flex;
  align-items: center;
  gap: 4px;
  color: #030303;
  text-align: left;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 27px;
`;

const Content = styled.main`
  width: 80vw;
  margin-top: 16px;
`;

const HiddenWrap = styled.div`
  position: fixed;
  top: 200%;
  left: 0;
`;

export default ReceiptMailModal;
